import GsuLoader from '@/components/GsuLoader';
import { Stack } from '@/components/Layout';
import { Aside, AsideWrapper } from '@/components/ResponsivePage';
import { PageMetaInfo } from '@/compositions/MetaInfo';
import { FragPageMetaInfo } from '@/compositions/MetaInfo/query';
import { HandleOnLoadAction } from '@/compositions/OnLoadAction';
import type { Props as PageComposerProps } from '@/compositions/PageComposer';
import { FragComposerSections } from '@/compositions/PageComposer/query';
import PageLayout from '@/compositions/PageLayout';
import { coreLayoutContainerAside, coreLayoutSection } from '@/constants/measured-scope';
import { queryGsuApi } from '@/service/query';
import { Page } from '@/shared/api-generated-types';
import EmptyStateCard from '@/widgets/EmptyStateCard';
import type { GetServerSideProps, NextPage } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import { useMemo } from 'react';
import { gql, useQuery } from 'urql';
export const gridContainer = "gahh8xa";
export const mainContainer = "madfi9z";

// PageComposer dynamic import to prevent SSR hydration issues
const PageComposerWithLoader = dynamic<PageComposerProps>(() => import('@/compositions/PageComposer'), {
  ssr: false,
  loading: () => <GsuLoader consumeSpace />
});
const PageComposer = dynamic<PageComposerProps>(() => import('@/compositions/PageComposer'), {
  ssr: false
});
const QUERY_NOTES = gql`
  query sessionNotesPage($request: SessionNotesPageRequest!) {
    ${FragComposerSections}
    ${FragPageMetaInfo}
    page: sessionNotesPage(request: $request) {
      title
      id
      metaInfo {
        ...PageMetaInfo
      }
      sections {
        ...CoreSections
        ...RecursiveSections
      }
    }
  }
`;
const Notes: NextPage<{
  sessionId: string;
}> = props => {
  const [{
    data,
    fetching
  }] = useQuery({
    query: QUERY_NOTES,
    variables: {
      request: {
        sessionId: props.sessionId
      }
    },
    requestPolicy: 'cache-first',
    pause: !props?.sessionId
  });
  const pageData: Page = data?.page || {};
  const hasSessionNotesDetails = useMemo(() => {
    try {
      return pageData?.sections?.find(section => section.__typename === 'SessionNotesDetailsPageSection');
    } catch (error) {
      return false;
    }
  }, [pageData]);
  const hasUpcomingSessions = useMemo(() => {
    try {
      return pageData?.sections?.find(section => section.__typename === 'UpcomingPageSection');
    } catch (error) {
      return false;
    }
  }, [pageData]);
  return <PageLayout>
      <PageMetaInfo {...pageData.metaInfo} />
      {pageData.onLoadAction ? <HandleOnLoadAction {...pageData.onLoadAction} /> : null}
      <section className={coreLayoutSection}>
        <div className={`${coreLayoutContainerAside}`}>
          <Stack y>
            <PageComposerWithLoader {...pageData} fetching={fetching} includeSections={['SessionNotesInfoPageSection']} />
            {hasSessionNotesDetails && <PageComposer fetching={fetching} {...pageData} includeSections={['SessionNotesDetailsPageSection']} />}
          </Stack>
          {hasUpcomingSessions ? <AsideWrapper>
              <Aside className="pt-md-lte-tablet pb-md">
                <PageComposer fetching={fetching} {...pageData} includeSections={['UpcomingPageSection']} />
              </Aside>
            </AsideWrapper> : <AsideWrapper>
              <Aside className="pt-md-lte-tablet pb-md">
                <EmptyStateCard title={'Upcoming Sessions'} messageContent={'We currently have no upcoming sessions for this class'} buttonLabel={'Browse Classes'} />
              </Aside>
            </AsideWrapper>}
        </div>
      </section>
    </PageLayout>;
};
export const getServerSideProps: GetServerSideProps = async ({
  locale = 'en-US',
  req,
  res,
  query: {
    sessionId
  }
}) => {
  const [urqlState, statusCode] = await queryGsuApi(QUERY_NOTES, {
    request: {
      sessionId
    }
  }, req);
  if (!!statusCode) {
    console.log('Notes page failed to retrieve details', statusCode);
    res.statusCode = statusCode;
    return {
      notFound: true
    };
  }
  return {
    props: {
      ...(await serverSideTranslations(locale, ['badge', 'common', 'classes'])),
      sessionId,
      urqlState
    }
  };
};
export default Notes;

require("./index.linaria.module.css!=!../../../../node_modules/@linaria/webpack-loader/node_modules/@linaria/webpack5-loader/lib/outputCssLoader.js?cacheProvider=!./index.tsx");